import { useState, useEffect } from 'react';
import { doc, getDoc, deleteDoc, collection, getDocs, writeBatch } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { deleteUser } from 'firebase/auth';
import { db, auth } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';

const cancelSubscriptionFn = httpsCallable(getFunctions(), 'cancelSubscription');

export default function Profile() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [vehicles, setVehicles] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    async function fetchProfile() {
      const snap = await getDoc(doc(db, 'users', currentUser.uid));
      if (snap.exists()) {
        const data = snap.data();
        setProfile(data.profile);
        setVehicles(data.vehicles || {});
      }
    }
    fetchProfile();
  }, [currentUser.uid]);

  async function handleLogout() {
    await logout();
    navigate('/');
  }

  async function handleDeleteAccount() {
    setDeleting(true);
    setDeleteError('');
    try {
      const uid = currentUser.uid;

      // Cancel Stripe subscription if active
      try {
        await cancelSubscriptionFn();
      } catch (e) {
        // Ignore — may not have a subscription
      }

      const batch = writeBatch(db);

      // Delete trips subcollection
      const tripsSnap = await getDocs(collection(db, 'users', uid, 'trips'));
      tripsSnap.docs.forEach((d) => batch.delete(d.ref));

      // Delete expenses subcollection
      const expensesSnap = await getDocs(collection(db, 'users', uid, 'expenses'));
      expensesSnap.docs.forEach((d) => batch.delete(d.ref));

      // Delete user document
      batch.delete(doc(db, 'users', uid));

      await batch.commit();

      // Delete Firebase Auth account
      await deleteUser(auth.currentUser);

      navigate('/');
    } catch (err) {
      console.error('Delete account error:', err);
      if (err.code === 'auth/requires-recent-login') {
        setDeleteError('For security, please sign out and sign back in, then try again.');
      } else {
        setDeleteError('Failed to delete account. Please try again.');
      }
    }
    setDeleting(false);
  }

  return (
    <div className="screen-container">
      <h2>Profile</h2>

      {profile && (
        <div className="profile-section">
          <div className="profile-info">
            <span className="profile-name">{profile.name}</span>
            <span className="profile-email">{profile.email}</span>
          </div>
        </div>
      )}

      <h3>Vehicles</h3>
      <div className="vehicle-list">
        {Object.entries(vehicles).map(([id, v]) => (
          <div key={id} className="vehicle-item">
            <span className="vehicle-name">{v.nickname || `${v.year} ${v.make} ${v.model}`}</span>
            <span className="vehicle-details">{v.year} {v.make} {v.model}</span>
          </div>
        ))}
      </div>
      <button className="btn btn-secondary" onClick={() => navigate('/vehicle-setup')}>
        Edit Vehicles
      </button>

      <div className="profile-actions">
        <button className="btn btn-danger" onClick={handleLogout}>
          Sign Out
        </button>
      </div>

      <div className="profile-legal">
        <Link to="/terms">Terms of Service</Link>
        <Link to="/privacy">Privacy Policy</Link>
      </div>

      <div className="profile-delete">
        <button className="btn-delete-account" onClick={() => setShowDeleteModal(true)}>
          Delete Account
        </button>
      </div>

      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => !deleting && setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Account</h3>
            <p>This will permanently delete your account and all associated data including:</p>
            <ul style={{ textAlign: 'left', margin: '12px 0', paddingLeft: '20px', fontSize: '14px', color: '#94A3B8' }}>
              <li>All trip records</li>
              <li>All expense records</li>
              <li>Vehicle information</li>
              <li>Profile data</li>
            </ul>
            <p><strong>This action cannot be undone.</strong></p>
            {deleteError && <div className="error-message">{deleteError}</div>}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={handleDeleteAccount} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
